// src/pages/Home.jsx - Backend Connected with Consistent Design
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap,
  TrendingUp,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Plus,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table.jsx';
import { apiCall } from '../utils/api';
import { canCreateConnector, isAnalystOrHigher } from '../utils/roles';

export default function Home() {
  const navigate = useNavigate();

  // State
  const [connectors, setConnectors] = useState([]);
  const [charts, setCharts] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [syncSummary, setSyncSummary] = useState({
    total_count: 0,
    success_count: 0,
    error_count: 0,
    success_rate: 100,
    total_rows_inserted: 0,
  });
  const [connectorHealth, setConnectorHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all data
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      
      try {
        // Load connectors
        const connectorsRes = await apiCall('/sync/configs');
        if (connectorsRes.ok) {
          const connectorsData = await connectorsRes.json();
          setConnectors(connectorsData.configs || []);
        }

        // Load charts
        const chartsRes = await apiCall('/charts/');
        if (chartsRes.ok) {
          const chartsData = await chartsRes.json();
          setCharts(chartsData.charts || []);
        }

        // Load recent sync runs (last 7 days) + summary stats
        const runsRes = await apiCall('/logs/sync-runs?limit=50&since=7d');
        if (runsRes.ok) {
          const runsData = await runsRes.json();
          setSyncRuns(runsData.runs || []);
          if (runsData.summary) {
            setSyncSummary(runsData.summary);
          }
        }

        // Try to load connector health (admin only - graceful fail)
        try {
          const healthRes = await apiCall('/admin/connector-health');
          if (healthRes.ok) {
            const healthData = await healthRes.json();
            setConnectorHealth(healthData.connectors || []);
          }
        } catch (err) {
          // Not admin - skip connector health
          console.log('Connector health not available (admin only)');
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Calculate stats from real data
  // 'active' = connector synced successfully in the last 7 days.
  // We derive this from connectorHealth (admin only) when available, else
  // from connectors that have at least one successful run in the recent
  // sync-runs window.
  const recentlySuccessfulConfigIds = new Set(
    syncRuns.filter(r => r.status === 'success').map(r => r.connector_config_id)
  );
  const activeConnectors = connectorHealth.length > 0
    ? connectorHealth.filter(c => c.status === 'healthy').length
    : connectors.filter(c => recentlySuccessfulConfigIds.has(c.id)).length;

  const stats = {
    activeConnectors,
    totalConnectors: connectors.length,
    successRate: syncSummary.success_rate,
    recentSyncs: syncSummary.total_count,
    totalRowsSynced: syncSummary.total_rows_inserted,
  };

  // Get recent activity
  const recentActivity = syncRuns
    .slice(0, 10)
    .map(run => ({
      id: run.id,
      connector: run.connector_display_name || 'Unknown',
      entity: run.entity || 'data',
      status: run.status,
      timestamp: run.finished_at || run.started_at,
      duration: run.duration_seconds,
    }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold tracking-tight text-white">Dashboard</h1>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            Dashboard
          </h1>
          <p className="text-sm text-[#a0a0a0] mt-1">
            {connectors.length === 0 
              ? 'Get started by adding your first connector'
              : `${stats.activeConnectors} active connectors syncing data`
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAnalystOrHigher() && (
            <Button variant="secondary" onClick={() => navigate('/charts/build')}>
              <BarChart3 className="w-4 h-4" />
              New Chart
            </Button>
          )}
          {canCreateConnector() && (
            <Button onClick={() => navigate('/connectors/new')}>
              <Plus className="w-4 h-4" />
              Add Connector
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          label="Active Connectors"
          value={stats.activeConnectors}
          subtitle={`${stats.totalConnectors} total`}
          icon={Zap}
          iconColor="text-blue-500"
          onClick={() => navigate('/connectors')}
        />
        <KPICard
          label="Success Rate"
          value={`${stats.successRate}%`}
          subtitle="Last 7 days"
          icon={TrendingUp}
          iconColor="text-green-500"
          onClick={() => navigate('/logs')}
        />
        <KPICard
          label="Syncs"
          value={stats.recentSyncs}
          subtitle="Last 7 days"
          icon={Activity}
          iconColor="text-violet-500"
          onClick={() => navigate('/logs')}
        />
        <KPICard
          label="Records Synced"
          value={formatNumber(stats.totalRowsSynced)}
          subtitle="Last 7 days"
          icon={CheckCircle}
          iconColor="text-amber-500"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity - Takes 2 columns */}
        <div className="lg:col-span-2">
          <RecentActivitySection activity={recentActivity} navigate={navigate} />
        </div>

        {/* Connector Health - Takes 1 column */}
        <div>
          <ConnectorHealthSection 
            connectors={
              connectorHealth.length > 0
                ? connectorHealth.filter(c => c.status === 'error' || c.status === 'warning')
                : connectors.filter(c => !recentlySuccessfulConfigIds.has(c.id))
            }
            navigate={navigate}
            hasHealthData={connectorHealth.length > 0}
            allHealthy={connectorHealth.length > 0 && connectorHealth.filter(c => c.status === 'error' || c.status === 'warning').length === 0}
          />
        </div>
      </div>

      {/* Recent Charts */}
      <RecentChartsSection charts={charts} navigate={navigate} />
    </div>
  );
}

// ==========================================
// KPI CARD
// ==========================================
function KPICard({ label, value, subtitle, icon: Icon, iconColor, onClick }) {
  return (
    <Card 
      className={onClick ? 'cursor-pointer hover:border-blue-500/50 transition-colors' : ''}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-[#a0a0a0] uppercase tracking-wide">
              {label}
            </p>
            <p className="text-2xl font-semibold text-white mt-2">
              {value}
            </p>
            <p className="text-xs text-[#6a6a6a] mt-1">
              {subtitle}
            </p>
          </div>
          <div className="w-10 h-10 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// RECENT ACTIVITY SECTION
// ==========================================
function RecentActivitySection({ activity, navigate }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Recent Activity</CardTitle>
          <button
            onClick={() => navigate('/logs')}
            className="text-sm text-blue-500 hover:text-blue-400 font-medium transition-colors"
          >
            View all
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {activity.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-[#6a6a6a] mx-auto mb-4" />
            <p className="text-sm font-medium text-white mb-1">
              No activity yet
            </p>
            <p className="text-sm text-[#6a6a6a]">
              Sync runs will appear here
            </p>
          </div>
        ) : (
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connector</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activity.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.connector}</TableCell>
                    <TableCell className="text-[#a0a0a0]">{item.entity}</TableCell>
                    <TableCell>
                      <StatusBadge status={item.status} />
                    </TableCell>
                    <TableCell className="text-[#a0a0a0]">
                      {formatTimestamp(item.timestamp)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// CONNECTOR HEALTH SECTION
// ==========================================
function ConnectorHealthSection({ connectors, navigate, hasHealthData, allHealthy }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Connector Health</CardTitle>
          <Badge variant="neutral">
            {connectors.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {connectors.length === 0 ? (
          allHealthy ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-sm font-medium text-white mb-1">
                All connectors healthy
              </p>
              <p className="text-sm text-[#6a6a6a]">
                No errors or warnings
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <Zap className="w-12 h-12 text-[#6a6a6a] mx-auto mb-4" />
              <p className="text-sm font-medium text-white mb-1">
                No connectors yet
              </p>
              <p className="text-sm text-[#6a6a6a] mb-4">
                Add your first connector
              </p>
              {canCreateConnector() && (
                <Button size="sm" onClick={() => navigate('/connectors/new')}>
                  <Plus className="w-4 h-4" />
                  Add Connector
                </Button>
              )}
            </div>
          )
        ) : (
          <div className="space-y-3">
            {connectors.slice(0, 5).map((connector) => (
              <div
                key={connector.id || connector.connector_config_id}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-[#1a1a1a] transition-colors cursor-pointer"
                onClick={() => navigate('/connectors')}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    hasHealthData
                      ? connector.status === 'healthy' ? 'bg-green-500' : connector.status === 'error' ? 'bg-red-500' : 'bg-amber-500'
                      : connector.sync_all_entities !== false ? 'bg-green-500' : 'bg-gray-500'
                  }`} />
                  <span className="text-sm text-white truncate">
                    {connector.display_name || `Connector #${connector.id || connector.connector_config_id}`}
                  </span>
                </div>
                {hasHealthData && connector.status && (
                  <HealthBadge status={connector.status} />
                )}
              </div>
            ))}
            {connectors.length > 5 && (
              <button
                onClick={() => navigate('/connectors')}
                className="w-full text-sm text-blue-500 hover:text-blue-400 transition-colors pt-2"
              >
                View all {connectors.length} connectors →
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// RECENT CHARTS SECTION
// ==========================================
function RecentChartsSection({ charts, navigate }) {
  const recentCharts = charts
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 4);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Recent Charts</CardTitle>
          <button
            onClick={() => navigate('/charts')}
            className="text-sm text-blue-500 hover:text-blue-400 font-medium transition-colors"
          >
            View all
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {recentCharts.length === 0 ? (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-[#6a6a6a] mx-auto mb-4" />
            <p className="text-sm font-medium text-white mb-1">
              No charts yet
            </p>
            <p className="text-sm text-[#6a6a6a] mb-4">
              Create your first visualization
            </p>
            <Button size="sm" onClick={() => navigate('/charts')}>
              <Plus className="w-4 h-4" />
              Create Chart
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentCharts.map((chart) => (
              <div
                key={chart.id}
                onClick={() => navigate('/charts')}
                className="p-4 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] hover:border-blue-500/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-white mb-1 truncate">
                  {chart.title || `Chart #${chart.id}`}
                </h3>
                <p className="text-xs text-[#6a6a6a]">
                  {chart.entity} · {chart.metric}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// STATUS BADGE
// ==========================================
function StatusBadge({ status }) {
  const config = {
    success: { variant: 'success', label: 'Success' },
    running: { variant: 'warning', label: 'Running' },
    error: { variant: 'error', label: 'Failed' },
    failed: { variant: 'error', label: 'Failed' },
  };

  const { variant, label } = config[status] || { variant: 'neutral', label: status };
  return <Badge variant={variant}>{label}</Badge>;
}

// ==========================================
// HEALTH BADGE
// ==========================================
function HealthBadge({ status }) {
  const config = {
    healthy: { variant: 'success', label: 'Healthy' },
    warning: { variant: 'warning', label: 'Warning' },
    error: { variant: 'error', label: 'Error' },
  };

  const { variant, label } = config[status] || { variant: 'neutral', label: 'Unknown' };
  return <Badge variant={variant} className="text-xs">{label}</Badge>;
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================
function calculateSuccessRate(syncRuns) {
  if (!syncRuns || syncRuns.length === 0) return 100;
  const successCount = syncRuns.filter(r => r.status === 'success').length;
  const rate = ((successCount / syncRuns.length) * 100).toFixed(0);
  return rate;
}

function calculateTotalEntities(connectors) {
  return connectors.reduce((total, connector) => {
    const entities = connector.entities || [];
    return total + entities.length;
  }, 0);
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function formatTimestamp(timestamp) {
  if (!timestamp) return 'Unknown';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}